/**
 * Datos fiscales del tenant en el panel. Refleja la regla de la API (`tenant-fiscal.ts`): antes de
 * asignar un plan o quitar la prueba deben estar completos. La API es la que decide; esto solo
 * avisa antes de intentarlo.
 */

export interface BillingFormValues {
  legalName: string;
  billingAddress: string;
  billingPostalCode: string;
  billingCity: string;
  billingProvince: string;
  billingCountry: string;
  erpCustomerCode: string;
  billingEmail: string;
  /** Vacío = sin tipo fiscal. */
  taxTypeId: string;
}

export const EMPTY_BILLING_VALUES: BillingFormValues = {
  legalName: "",
  billingAddress: "",
  billingPostalCode: "",
  billingCity: "",
  billingProvince: "",
  billingCountry: "ES",
  erpCustomerCode: "",
  billingEmail: "",
  taxTypeId: "",
};

/** Campos exigidos antes de facturar (sin razón social ni país: caen en el nombre y en ES). */
const REQUIRED: Array<{ key: keyof BillingFormValues; label: string }> = [
  { key: "billingAddress", label: "Domicilio" },
  { key: "billingPostalCode", label: "Código postal" },
  { key: "billingCity", label: "Población" },
  { key: "billingProvince", label: "Provincia" },
  { key: "billingEmail", label: "Correo de facturación" },
  { key: "taxTypeId", label: "Tipo fiscal" },
  { key: "erpCustomerCode", label: "Código de cliente del ERP" },
];

/** Etiquetas de los campos fiscales que faltan (el CIF es obligatorio siempre, no se lista). */
export function missingBillingFields(values: BillingFormValues): string[] {
  return REQUIRED.filter(({ key }) => values[key].trim() === "").map(({ label }) => label);
}

/** Valores del formulario a partir del detalle del tenant (null -> vacío; país por defecto ES). */
export function billingValuesFromTenant(tenant: {
  legalName?: string | null;
  billingAddress?: string | null;
  billingPostalCode?: string | null;
  billingCity?: string | null;
  billingProvince?: string | null;
  billingCountry?: string | null;
  erpCustomerCode?: string | null;
  billingEmail?: string | null;
  taxTypeId?: string | null;
}): BillingFormValues {
  return {
    legalName: tenant.legalName ?? "",
    billingAddress: tenant.billingAddress ?? "",
    billingPostalCode: tenant.billingPostalCode ?? "",
    billingCity: tenant.billingCity ?? "",
    billingProvince: tenant.billingProvince ?? "",
    billingCountry: tenant.billingCountry ?? "ES",
    erpCustomerCode: tenant.erpCustomerCode ?? "",
    billingEmail: tenant.billingEmail ?? "",
    taxTypeId: tenant.taxTypeId ?? "",
  };
}

/**
 * Cuerpo para el PATCH: vacío = null (quitarlo). El país vacío se envía como null (la API asume ES).
 */
export function billingValuesToPatch(values: BillingFormValues): Record<string, string | null> {
  const orNull = (v: string): string | null => (v.trim() === "" ? null : v.trim());
  return {
    legalName: orNull(values.legalName),
    billingAddress: orNull(values.billingAddress),
    billingPostalCode: orNull(values.billingPostalCode),
    billingCity: orNull(values.billingCity),
    billingProvince: orNull(values.billingProvince),
    billingCountry: orNull(values.billingCountry)?.toUpperCase() ?? null,
    erpCustomerCode: orNull(values.erpCustomerCode),
    billingEmail: orNull(values.billingEmail),
    taxTypeId: orNull(values.taxTypeId),
  };
}

/** Cuerpo para el alta: solo los campos con valor (la API los trata como opcionales). */
export function billingValuesToCreate(values: BillingFormValues): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(billingValuesToPatch(values))) {
    if (value !== null) out[key] = value;
  }
  return out;
}
